import { useState, useCallback, useEffect, useRef } from "react";
import type { CollectionEntityEntry } from "@/utils/entityMappers";

interface UseEntityConfirmationFlowOptions {
  entities: CollectionEntityEntry[];
  hasEntities: boolean;
  isFetching: boolean;
  /** Called when the flow resolves: either immediately (no entities) or after confirm/skip. */
  onAdd: (includeEntities: boolean, selectedEntityIds: Set<string>) => void;
}

export function useEntityConfirmationFlow({
  entities,
  hasEntities,
  isFetching,
  onAdd,
}: UseEntityConfirmationFlowOptions) {
  const [step, setStep] = useState<"select" | "confirm">("select");
  const [selectedEntityIds, setSelectedEntityIds] = useState<Set<string>>(new Set());
  const onAddRef = useRef(onAdd);
  onAddRef.current = onAdd;

  const startConfirmation = useCallback(() => {
    setSelectedEntityIds(new Set(entities.map((e) => e.entity_id)));
    setStep("confirm");
  }, [entities]);

  // Pre-select all entities once they arrive
  useEffect(() => {
    if (step === "confirm" && hasEntities) {
      setSelectedEntityIds(new Set(entities.map((e) => e.entity_id)));
    }
  }, [step, hasEntities, entities]);



  const toggleEntity = useCallback((entityId: string) => {
    setSelectedEntityIds((prev) => {
      const next = new Set(prev);
      if (next.has(entityId)) next.delete(entityId);
      else next.add(entityId);
      return next;
    });
  }, []);

  const toggleAll = useCallback(() => {
    const allSelected = entities.length > 0 && selectedEntityIds.size === entities.length;
    setSelectedEntityIds(
      allSelected ? new Set() : new Set(entities.map((e) => e.entity_id)),
    );
  }, [entities, selectedEntityIds]);

  const toggleGroup = useCallback((groupEntityIds: string[]) => {
    setSelectedEntityIds((prev) => {
      const next = new Set(prev);
      const allIn = groupEntityIds.every((id) => next.has(id));
      if (allIn) {
        for (const id of groupEntityIds) next.delete(id);
      } else {
        for (const id of groupEntityIds) next.add(id);
      }
      return next;
    });
  }, []);

  const confirmAdd = useCallback(() => {
    onAdd(selectedEntityIds.size > 0, selectedEntityIds);
  }, [onAdd, selectedEntityIds]);

  const goBack = useCallback(() => {
    setStep("select");
  }, []);

  const reset = useCallback(() => {
    setStep("select");
    setSelectedEntityIds(new Set());
  }, []);

  return {
    step,
    selectedEntityIds,
    startConfirmation,
    toggleEntity,
    toggleGroup,
    toggleAll,
    confirmAdd,
    goBack,
    reset,
  };
}
